import React, {
  useState,
  useContext,
  useImperativeHandle,
  forwardRef,
  useRef,
} from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { Icon, Fade, IconButton, Card } from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Swal from "sweetalert2";
import { UserContext } from "App";
import { LanguageContext } from "App";
import { useTenant } from "../../context/TenantContext";
import SessionsApi from "Classes/API/Administration/SessionsApi";
import CreatePatientHistoryEntryReq from "../../Classes/Models/Requests/Users/CreatePatientHistoryEntryReq";
import { getDownloadURL, getStorage, ref, uploadBytes } from "firebase/storage";
import { v4 as uuidv4 } from "uuid";
import toast from "react-hot-toast";

const CreateEntry = forwardRef((props, refProp) => {
  const { id } = useParams();
  const { translations } = useContext(LanguageContext);
  const location = useLocation();
  const navigate = useNavigate();
  const { accSessionId, therapistId: stateTherapistId } = location.state || {};
  const User = useContext(UserContext);
  const { currentTenant } = useTenant();
  const storage = getStorage();

  const [notes, setNotes] = useState("");
  const [selectedFileName, setSelectedFileName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const fileInputRef = useRef();

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFileName(file.name);
    }
  };

  const handleRemoveFile = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setSelectedFileName("");
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const uploadFileData = async (file) => {
    const tenantId = currentTenant?.id || "Global";
    const uniqueId = uuidv4();
    const filePath = `${tenantId}/PatientFiles/Patient${id}/${uniqueId}`;
    const storageRef = ref(storage, filePath);
    const metadata = { contentType: file.type };
    const snapshot = await uploadBytes(storageRef, file, metadata);
    const downloadURL = await getDownloadURL(snapshot.ref);
    return { downloadURL, filePath };
  };

  const hasChanges = notes.trim() !== "" || selectedFileName !== "";

  const submitForm = async () => {
    const file = fileInputRef.current?.files[0];

    if (!notes.trim() && !file) {
      toast.error("Please add a note or an attachment before saving.");
      return false;
    }

    setSubmitting(true);
    try {
      const result = await toast.promise(
        (async () => {
          let fileUrl = null;
          let filePath = null;

          if (file) {
            const fileData = await uploadFileData(file);
            fileUrl = fileData.downloadURL;
            filePath = fileData.filePath;
          }

          const req = new CreatePatientHistoryEntryReq();
          req.Notes = notes;
          req.PatientId = id;
          req.FileUrl = fileUrl;
          req.FilePath = filePath;
          req.FileType = file?.type || null;
          req.FileName = file?.name || null;
          req.CreatedById = User.id;
          req.TherapistId =
            stateTherapistId || User.therapistId || User.getTherapist?.id;
          req.AccountingSessionId = accSessionId;

          const res = await SessionsApi.CreatePatientHistoryEntry2(req);

          if (res.status !== "Success") {
            throw new Error(res.message || "Failed to save entry to database");
          }

          return true;
        })(),
        {
          loading: file
            ? "Uploading attachments and saving..."
            : "Saving clinical entry...",
          success: "Saved successfully!",
          error: (err) => `Save failed: ${err.message}`,
        },
      );
      return result;
    } catch (error) {
      return false;
    } finally {
      setSubmitting(false);
    }
  };

  useImperativeHandle(refProp, () => ({
    submit: submitForm,
  }));

  const handleBackClick = () => {
    if (hasChanges) {
      Swal.fire({
        title: "Unsaved Changes",
        text: "You have unsaved changes. You must submit first or discard your changes.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#d33",
        cancelButtonColor: "#3085d6",
        confirmButtonText: "Discard Changes",
        cancelButtonText: "Keep Editing",
      }).then((result) => {
        if (result.isConfirmed) {
          navigate(-1);
        }
      });
    } else {
      navigate(-1);
    }
  };

  const handleSubmitClick = async () => {
    const success = await submitForm();
    if (success) {
      navigate(-1);
    }
  };

  const renderFormContent = () => (
    <MDBox display="flex" flexDirection="column" gap={4} py={2}>
      <MDBox>
        <MDTypography variant="h5" fontWeight="bold" color="dark">
          {translations?.Addentry || "New Clinical Entry"}
        </MDTypography>
        <MDTypography variant="button" color="text" fontWeight="regular">
          Complete the session documentation and attach any relevant files.
        </MDTypography>
      </MDBox>

      <MDBox>
        <MDTypography
          variant="caption"
          fontWeight="bold"
          color="info"
          textTransform="uppercase"
          mb={1}
          display="block"
        >
          {translations?.UploadFiles || "Attachments"}
        </MDTypography>
        <MDBox
          component="label"
          htmlFor="file-upload"
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            minHeight: "180px",
            width: "100%",
            border: "2px dashed",
            borderColor: selectedFileName ? "info.main" : "#d2d6da",
            borderRadius: "16px",
            backgroundColor: selectedFileName ? "#f0f7ff" : "#f8f9fa",
            cursor: "pointer",
            position: "relative",
            transition: "all 300ms ease",
            "&:hover": {
              borderColor: "info.main",
              backgroundColor: "#f0f7ff",
              transform: "translateY(-2px)",
            },
          }}
        >
          {selectedFileName && (
            <IconButton
              size="small"
              onClick={handleRemoveFile}
              sx={{
                position: "absolute",
                top: 12,
                right: 12,
                backgroundColor: "#fff",
                boxShadow: 2,
                zIndex: 2,
                "&:hover": { backgroundColor: "#ffebee" },
              }}
            >
              <Icon color="error">close</Icon>
            </IconButton>
          )}

          <Icon
            fontSize="large"
            color={selectedFileName ? "info" : "secondary"}
            sx={{ mb: 1 }}
          >
            {selectedFileName ? "task_check" : "cloud_upload"}
          </Icon>

          {selectedFileName ? (
            <Fade in={true}>
              <MDBox textAlign="center" px={2}>
                <MDTypography
                  variant="subtitle2"
                  fontWeight="bold"
                  color="info"
                >
                  {selectedFileName}
                </MDTypography>
                <MDTypography variant="caption" color="text">
                  File ready for upload. Click to change.
                </MDTypography>
              </MDBox>
            </Fade>
          ) : (
            <MDBox textAlign="center">
              <MDTypography variant="button" fontWeight="bold" color="dark">
                Browse Files or Drag & Drop
              </MDTypography>
              <MDTypography variant="caption" color="text" display="block">
                PDF, PNG, JPG (Max 10MB)
              </MDTypography>
            </MDBox>
          )}

          <input
            id="file-upload"
            type="file"
            ref={fileInputRef}
            onChange={handleFileChange}
            hidden
          />
        </MDBox>
      </MDBox>

      <MDBox>
        <MDTypography
          variant="caption"
          fontWeight="bold"
          color="info"
          textTransform="uppercase"
          mb={1}
          display="block"
        >
          Notes
        </MDTypography>
        <MDInput
          fullWidth
          multiline
          rows={6}
          label="Enter Notes"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: "12px",
              backgroundColor: "#fff",
              "&.Mui-focused": {
                boxShadow: "0 0 0 3px rgba(50, 134, 251, 0.1)",
              },
            },
          }}
        />
      </MDBox>
    </MDBox>
  );

  const isDialog = props.isDialog;

  if (isDialog) {
    return renderFormContent();
  }

  return (
    <MDBox pt={3} pb={3}>
      <Card sx={{ borderRadius: "16px", border: "1px solid #f0f2f5" }}>
        <MDBox p={4}>
          <MDBox
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={4}
          >
            <MDButton
              variant="outlined"
              color="dark"
              onClick={handleBackClick}
              disabled={submitting}
            >
              <Icon>arrow_back</Icon>&nbsp;Back
            </MDButton>
            <MDTypography variant="h4" fontWeight="bold">
              New Clinical Entry
            </MDTypography>
            <MDButton
              variant="gradient"
              color="success"
              onClick={handleSubmitClick}
              disabled={submitting}
            >
              {submitting ? "Submitting..." : "Submit Entry"}
            </MDButton>
          </MDBox>

          {renderFormContent()}
        </MDBox>
      </Card>
    </MDBox>
  );
});

export default CreateEntry;
